import { useState, useEffect } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Sparkles, Play, CheckCircle2, Trash2, RefreshCw, Star, FileText, BookOpen, ClipboardList, Users, Eye, ShieldCheck, ShieldAlert, ExternalLink, Loader2, Copy } from "lucide-react";

const API = import.meta.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "";

const FORMAT_META = {
  whitepaper:   { label: "Whitepaper",     icon: FileText,      color: "bg-cyan-100 text-cyan-700"       },
  ebook:        { label: "eBook",          icon: BookOpen,      color: "bg-purple-100 text-purple-700"   },
  buyers_guide: { label: "Buyer's Guide",  icon: ClipboardList, color: "bg-emerald-100 text-emerald-700" },
  case_study:   { label: "Case Study",     icon: Star,          color: "bg-amber-100 text-amber-700"     },
  newsletter:   { label: "Newsletter",     icon: FileText,      color: "bg-rose-100 text-rose-700"       },
};

const CLIENT_LOGOS_SEED = [
  "Adobe","Cloudflare","Cloudera","T-Mobile","AMD","ADP","VMware","SAP",
  "MicroStrategy","Salesforce","Oracle","Microsoft","Intel","HP","Nutanix",
  "NetApp","IBM","Dropbox","Box","Cisco","Akamai","Genesys","Freshworks","Symantec",
];

export default function AdminSbtAIAutopilot() {
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState({});
  const [drafts, setDrafts] = useState([]);
  const [availableIndustries, setAvailableIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningNow, setRunningNow] = useState(false);
  const [caseStudyCompanies, setCaseStudyCompanies] = useState(CLIENT_LOGOS_SEED.slice(0, 5));
  const [csRunning, setCsRunning] = useState(false);
  const [preview, setPreview] = useState(null);          // full draft object shown in preview modal
  const [checkingId, setCheckingId] = useState(null);    // id currently running plagiarism check

  const token = () => localStorage.getItem("token");
  const hdr = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${token()}` });

  const load = async () => {
    setLoading(true);
    try {
      const [cr, sr, dr, ir] = await Promise.all([
        fetch(`${API}/api/admin/sbt/content-farm/config`,  { headers: hdr() }),
        fetch(`${API}/api/admin/sbt/content-farm/status`,  { headers: hdr() }),
        fetch(`${API}/api/admin/sbt/content-farm/drafts?limit=200`, { headers: hdr() }),
        fetch(`${API}/api/admin/sbt/content-farm/available-industries`, { headers: hdr() }),
      ]);
      setConfig(await cr.json());
      setStatus(await sr.json());
      const dj = await dr.json();
      setDrafts(dj.drafts || []);
      const ij = await ir.json();
      setAvailableIndustries(ij.industries || []);
    } catch { toast.error("Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveConfig = async () => {
    try {
      const r = await fetch(`${API}/api/admin/sbt/content-farm/config`, { method: "PUT", headers: hdr(), body: JSON.stringify(config) });
      setConfig(await r.json());
      toast.success("Config saved");
    } catch { toast.error("Save failed"); }
  };

  const runNow = async () => {
    setRunningNow(true);
    try {
      await fetch(`${API}/api/admin/sbt/content-farm/run-now`, { method: "POST", headers: hdr() });
      toast.success("Batch started — drafts will appear in ~30-90s. Refresh to see them.");
      setTimeout(load, 12000);
    } catch { toast.error("Run failed"); }
    finally { setRunningNow(false); }
  };

  const genCaseStudies = async () => {
    setCsRunning(true);
    try {
      await fetch(`${API}/api/admin/sbt/case-studies/generate`, {
        method: "POST", headers: hdr(),
        body: JSON.stringify({ companies: caseStudyCompanies, use_case: "demand generation" }),
      });
      toast.success(`Queued ${caseStudyCompanies.length} illustrative case studies`);
      setTimeout(load, 10000);
    } catch { toast.error("Case-study run failed"); }
    finally { setCsRunning(false); }
  };

  const genCaseStudiesBalanced = async () => {
    setCsRunning(true);
    try {
      const r = await fetch(`${API}/api/admin/sbt/case-studies/generate`, {
        method: "POST", headers: hdr(),
        body: JSON.stringify({ balanced: true, limit: 10, use_case: "demand generation" }),
      });
      const j = await r.json();
      if (!j.ok) {
        toast.error(j.detail || "Balanced generation failed");
      } else {
        const industries = [...new Set((j.queued || []).map((q) => q.industry))].length;
        toast.success(`Queued ${j.queued?.length || 0} case studies across ${industries} industries`);
        setTimeout(load, 10000);
      }
    } catch { toast.error("Balanced run failed"); }
    finally { setCsRunning(false); }
  };

  const approve = async (id, destination = "resources") => {
    const url = `${API}/api/admin/sbt/content-farm/drafts/${id}/approve?destination=${encodeURIComponent(destination)}`;
    const r = await fetch(url, { method: "POST", headers: hdr() });
    if (r.status === 409) {
      const err = await r.json();
      if (window.confirm(err.detail + "\n\nForce-publish anyway?")) {
        await fetch(`${url}&force=true`, { method: "POST", headers: hdr() });
      } else {
        toast.error("Publish blocked by originality gate");
        return;
      }
    } else if (!r.ok) {
      toast.error("Publish failed");
      return;
    }
    setDrafts(drafts.map((d) => d.id === id ? { ...d, status: "published", publish_destination: destination } : d));
    toast.success(destination === "market_plus" ? "Published to Market Plus" : "Published to Resources");
  };
  const reject = async (id) => {
    if (!window.confirm("Delete this AI draft permanently?")) return;
    await fetch(`${API}/api/admin/sbt/content-farm/drafts/${id}`, { method: "DELETE", headers: hdr() });
    setDrafts(drafts.filter((d) => d.id !== id));
    toast.success("Rejected");
  };

  const openPreview = async (id) => {
    setPreview({ loading: true });
    try {
      const r = await fetch(`${API}/api/admin/sbt/content-farm/drafts/${id}`, { headers: hdr() });
      if (!r.ok) throw new Error(await r.text());
      setPreview(await r.json());
    } catch { setPreview(null); toast.error("Preview failed"); }
  };

  const checkOriginality = async (id) => {
    setCheckingId(id);
    try {
      const r = await fetch(`${API}/api/admin/sbt/content-farm/drafts/${id}/plagiarism-check`, { method: "POST", headers: hdr() });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.detail || "check failed"); }
      const j = await r.json();
      // Update the local card with the fresh score
      setDrafts(drafts.map((d) => d.id === id ? { ...d, source_meta: { ...(d.source_meta || {}), plagiarism: j } } : d));
      toast.success(`Originality: ${j.score}/100${j.phrases_duplicated ? ` · ${j.phrases_duplicated} matches` : ""}`);
    } catch (e) { toast.error("Originality check failed: " + e.message); }
    finally { setCheckingId(null); }
  };

  const [regenBusy, setRegenBusy] = useState({});      // per-draft {id: bool}
  const [regenAllStatus, setRegenAllStatus] = useState(null);

  const regenerateOne = async (id) => {
    if (!window.confirm("Rewrite this draft through the latest Forrester/Gartner quality pipeline? The title + body will change.")) return;
    setRegenBusy((s) => ({ ...s, [id]: true }));
    try {
      const r = await fetch(`${API}/api/admin/sbt/content-farm/drafts/${id}/regenerate`, { method: "POST", headers: hdr() });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.detail || "regen failed"); }
      const j = await r.json();
      toast.success(`Regenerated (quality ${j.quality?.final_score}/100, ${j.quality?.attempts} attempt${j.quality?.attempts === 1 ? "" : "s"})`);
      await load();
    } catch (e) { toast.error("Regenerate failed: " + e.message); }
    finally { setRegenBusy((s) => ({ ...s, [id]: false })); }
  };

  const regenerateAll = async () => {
    if (!window.confirm("Rewrite ALL unpublished drafts through the analyst-grade pipeline? This may take several minutes and consumes Claude tokens.")) return;
    try {
      const r = await fetch(`${API}/api/admin/sbt/content-farm/drafts/regenerate-all`, {
        method: "POST", headers: hdr(),
        body: JSON.stringify({}),
      });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.detail || "failed"); }
      const j = await r.json();
      setRegenAllStatus(j);
      toast.success(j.already_running ? "A regeneration is already running." : `Regenerating ${j.total} drafts…`);
      // Poll status every 3s
      const poll = setInterval(async () => {
        try {
          const rs = await fetch(`${API}/api/admin/sbt/content-farm/regenerate/status`, { headers: hdr() });
          const js = await rs.json();
          setRegenAllStatus(js);
          if (!js.running) { clearInterval(poll); await load(); toast.success(`Regeneration complete — ${js.processed} processed, ${js.errors?.length || 0} errors`); }
        } catch { clearInterval(poll); }
      }, 3000);
    } catch (e) { toast.error("Batch regenerate failed: " + e.message); }
  };

  if (loading || !config) return <div className="py-10 text-center text-slate-500">Loading AI Autopilot…</div>;

  return (
    <div className="space-y-6" data-testid="admin-sbt-autopilot">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-cyan-600" /> AI Content Autopilot
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            Nightly whitepaper + eBook + buyer&apos;s guide drafts (10/day) plus on-demand illustrative case studies. Every draft holds for approval.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={regenerateAll} data-testid="regenerate-all-btn" disabled={regenAllStatus?.running}>
            {regenAllStatus?.running
              ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Rewriting {regenAllStatus.processed}/{regenAllStatus.total}…</>
              : <><Sparkles className="w-4 h-4 mr-1" /> Rewrite all drafts to Forrester standard</>}
          </Button>
          <Button variant="outline" onClick={load} data-testid="autopilot-refresh"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
        </div>
      </div>

      {regenAllStatus?.running && (
        <div className="rounded-lg border border-cyan-200 bg-cyan-50 px-4 py-3 text-sm text-cyan-900 flex items-center gap-3" data-testid="regen-progress-banner">
          <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
          <div className="flex-1">
            <div className="font-semibold">Rewriting drafts through the analyst-grade pipeline…</div>
            <div className="text-xs text-cyan-800 mt-0.5">
              {regenAllStatus.processed} / {regenAllStatus.total} processed
              {regenAllStatus.last_title && <> · Last: &ldquo;{regenAllStatus.last_title}&rdquo;</>}
              {regenAllStatus.errors?.length ? ` · ${regenAllStatus.errors.length} error(s)` : ""}
            </div>
          </div>
        </div>
      )}

      <Tabs defaultValue="drafts">
        <TabsList>
          <TabsTrigger value="drafts"       data-testid="ap-tab-drafts">Draft Inbox ({drafts.length})</TabsTrigger>
          <TabsTrigger value="content_farm" data-testid="ap-tab-farm">Content Farm</TabsTrigger>
          <TabsTrigger value="case_studies" data-testid="ap-tab-cs">Case Studies</TabsTrigger>
          <TabsTrigger value="newsletter"   data-testid="ap-tab-newsletter">Newsletter</TabsTrigger>
          <TabsTrigger value="config"       data-testid="ap-tab-config">Config</TabsTrigger>
        </TabsList>

        {/* ── DRAFT INBOX ── */}
        <TabsContent value="drafts" className="mt-4 space-y-3">
          {drafts.length === 0 && (
            <div className="text-xs text-slate-400 italic border border-dashed rounded-lg p-10 text-center">
              No AI drafts yet. Kick off a Content Farm run or generate some case studies.
            </div>
          )}
          {drafts.map((d) => {
            const sm = d.source_meta || {};
            const fmt = sm.format || (
              sm.generator === "case_study_generator" ? "case_study"
              : sm.generator === "newsletter_in_a_box" ? "newsletter"
              : "whitepaper"
            );
            const meta = FORMAT_META[fmt] || { label: fmt, color: "bg-slate-100 text-slate-700", icon: FileText };
            const Icon = meta.icon;
            const plag = sm.plagiarism;
            const score = plag && typeof plag.score === "number" ? plag.score : null;
            const scoreColor = score === null ? "" : score >= 90 ? "bg-emerald-100 text-emerald-700 border-emerald-300"
                                                     : score >= 70 ? "bg-amber-100 text-amber-700 border-amber-300"
                                                     :                 "bg-red-100 text-red-700 border-red-300";
            const ScoreIcon = score === null ? null : score >= 70 ? ShieldCheck : ShieldAlert;
            return (
              <Card key={d.id} data-testid={`draft-${d.id}`}>
                <CardContent className="pt-5 space-y-2">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded ${meta.color}`}>
                        <Icon className="w-3 h-3" /> {meta.label}
                      </span>
                      {d.status === "published" ? (
                        <>
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-300" data-testid={`draft-status-${d.id}`}>
                            <CheckCircle2 className="w-3 h-3" /> Published
                          </span>
                          <span className={`inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded border ${(d.publish_destination || "resources") === "market_plus" ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "bg-cyan-50 text-cyan-700 border-cyan-200"}`} data-testid={`draft-destination-${d.id}`}>
                            {(d.publish_destination || "resources") === "market_plus" ? "Market Plus" : "Resources"}
                          </span>
                        </>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-300" data-testid={`draft-status-${d.id}`}>
                          Draft
                        </span>
                      )}
                      {sm.illustrative && (
                        <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                          Illustrative
                        </span>
                      )}
                      {score !== null && ScoreIcon && (
                        <button
                          onClick={() => setPreview({ ...d, _focusPlagiarism: true, loading: false })}
                          className={`inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded border ${scoreColor}`}
                          data-testid={`draft-score-${d.id}`}
                          title="Click to see matched sources"
                        >
                          <ScoreIcon className="w-3 h-3" /> Originality {score}/100
                        </button>
                      )}
                      {sm.industry && <span className="text-xs text-slate-500">· {sm.industry}</span>}
                      {sm.company && <span className="text-xs text-slate-500">· {sm.company}</span>}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button variant="outline" size="sm" onClick={() => openPreview(d.id)} data-testid={`draft-preview-${d.id}`}>
                        <Eye className="w-3.5 h-3.5 mr-1" /> Preview
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => checkOriginality(d.id)} disabled={checkingId === d.id} data-testid={`draft-check-${d.id}`}>
                        {checkingId === d.id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5 mr-1" />}
                        {checkingId === d.id ? "Checking…" : (score === null ? "Check originality" : "Re-check")}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => reject(d.id)} data-testid={`draft-reject-${d.id}`}>
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Reject
                      </Button>
                      {d.status !== "published" && (
                        <Button variant="outline" size="sm" onClick={() => regenerateOne(d.id)} disabled={!!regenBusy[d.id]} data-testid={`draft-regenerate-${d.id}`} title="Rewrite through analyst-grade pipeline">
                          {regenBusy[d.id] ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                          {regenBusy[d.id] ? "Rewriting…" : "Rewrite"}
                        </Button>
                      )}
                      {d.status !== "published" && (
                        <>
                          <Button size="sm" onClick={() => approve(d.id, "resources")} data-testid={`draft-approve-resources-${d.id}`}>
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Publish to Resources
                          </Button>
                          <Button size="sm" variant="secondary" onClick={() => approve(d.id, "market_plus")} data-testid={`draft-approve-marketplus-${d.id}`}>
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Publish to Market Plus
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-slate-900">{d.title}</div>
                  <div className="text-xs text-slate-600 leading-relaxed">{d.abstract}</div>
                  <div className="text-[11px] text-slate-400">
                    /resources/detail/?slug={d.slug} · {sm.generated_at ? new Date(sm.generated_at).toLocaleString() : ""}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        {/* ── CONTENT FARM ── */}
        <TabsContent value="content_farm" className="mt-4 space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Trending-topic content pipeline</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="text-sm text-slate-600">
                Each run picks the top {config.topics_per_run} trending topics from the existing <code className="text-xs px-1.5 py-0.5 rounded bg-slate-100">sbt_trending_topics</code> collection and drafts <strong>whitepaper + eBook + buyer&apos;s guide</strong> per topic — {config.topics_per_run * 3} assets per cycle.
              </div>
              <div className="text-xs text-slate-500">
                Last run: <strong>{status.last_finished_at || "never"}</strong> · Assets created: <strong>{status.last_assets_created || 0}</strong>
                {status.last_error && <span className="text-red-500 ml-2">· {status.last_error}</span>}
              </div>
              <Button onClick={runNow} disabled={runningNow || status.running} data-testid="autopilot-run-now">
                <Play className="w-4 h-4 mr-1" /> {runningNow || status.running ? "Running…" : "Run one batch now"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── CASE STUDIES ── */}
        <TabsContent value="case_studies" className="mt-4 space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4" /> Illustrative case study generator</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="text-sm text-slate-600">
                Generates a template case study per Trusted-by logo. Every draft carries an <strong>Illustrative — pending client confirmation</strong> disclaimer until an admin toggles it to Confirmed via the Landing Pages editor.
              </div>
              <div>
                <Label className="text-xs">Companies (comma-separated — from your Trusted-by slider)</Label>
                <Input
                  value={caseStudyCompanies.join(", ")}
                  onChange={(e) => setCaseStudyCompanies(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                  data-testid="cs-companies"
                />
                <div className="text-[11px] text-slate-400 mt-1">Suggested: {CLIENT_LOGOS_SEED.slice(0, 10).join(", ")}…</div>
              </div>
              <Button onClick={genCaseStudies} disabled={csRunning || caseStudyCompanies.length === 0} data-testid="cs-generate">
                <Play className="w-4 h-4 mr-1" /> {csRunning ? "Generating…" : `Generate ${caseStudyCompanies.length} case studies (custom list)`}
              </Button>
              <div className="mt-3 pt-3 border-t border-slate-200">
                <div className="text-xs text-slate-500 mb-2">
                  <strong>Balanced mode</strong> — picks one company per industry (SaaS, Security, Semiconductor, Telecom, Financial Services, Healthcare, Retail, Manufacturing, Media, Public Sector), automatically skipping companies that already have a draft. Best for keeping your Content Library evenly distributed across verticals.
                </div>
                <Button
                  onClick={genCaseStudiesBalanced}
                  disabled={csRunning}
                  variant="outline"
                  className="border-cyan-300 text-cyan-800 hover:bg-cyan-50"
                  data-testid="cs-generate-balanced"
                >
                  <Play className="w-4 h-4 mr-1" /> {csRunning ? "Generating…" : "Generate 10 balanced case studies (auto-pick)"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── NEWSLETTER IN A BOX ── */}
        <TabsContent value="newsletter" className="mt-4">
          <NewsletterPanel hdr={hdr} onGenerated={load} />
        </TabsContent>

        {/* ── CONFIG ── */}
        <TabsContent value="config" className="mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Autopilot configuration</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-semibold">Enable nightly content farm</Label>
                  <div className="text-xs text-slate-500">Off = drafts only generated on manual Run.</div>
                </div>
                <Switch checked={!!config.enabled} onCheckedChange={(v) => setConfig({ ...config, enabled: v })} data-testid="config-enabled" />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-semibold">Auto originality check on generated drafts</Label>
                  <div className="text-xs text-slate-500">Consumes ~5 SerpAPI credits per draft (~50/day at 10-draft cadence). SerpAPI free tier = 100/month.</div>
                </div>
                <Switch checked={!!config.auto_originality_check} onCheckedChange={(v) => setConfig({ ...config, auto_originality_check: v })} data-testid="config-auto-plag" />
              </div>

              {status.serpapi_used_this_month !== undefined && (
                <div className="text-xs bg-slate-50 border border-slate-200 rounded-md p-3 flex items-center justify-between">
                  <span className="text-slate-600">
                    SerpAPI credits used this month <strong className="text-slate-900">({status.serpapi_month || "—"})</strong>:
                  </span>
                  <span className={`font-bold ${status.serpapi_used_this_month > 90 ? "text-red-600" : status.serpapi_used_this_month > 70 ? "text-amber-600" : "text-emerald-600"}`}>
                    {status.serpapi_used_this_month}
                  </span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Topics per run</Label>
                  <Input type="number" min={1} max={10} value={config.topics_per_run} onChange={(e) => setConfig({ ...config, topics_per_run: parseInt(e.target.value) || 3 })} data-testid="config-topics" />
                  <div className="text-[11px] text-slate-400 mt-1">→ {(config.topics_per_run || 3) * 3} assets/day</div>
                </div>
                <div>
                  <Label className="text-xs">Run interval (seconds)</Label>
                  <Input type="number" min={3600} value={config.run_interval_seconds} onChange={(e) => setConfig({ ...config, run_interval_seconds: parseInt(e.target.value) || 86400 })} data-testid="config-interval" />
                  <div className="text-[11px] text-slate-400 mt-1">86400 = 24h</div>
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <Label className="text-sm font-semibold">Allowed industries (whitelist)</Label>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Content Farm only samples topics from these industries. Leave empty to allow all {availableIndustries.length} industries in the trending-topics database.
                      Currently selecting <strong>{(config.allowed_industries || []).length}</strong>.
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setConfig({ ...config, allowed_industries: [] })}
                      className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50"
                      data-testid="config-industries-clear"
                    >Clear all</button>
                    <button
                      onClick={() => setConfig({ ...config, allowed_industries: [...availableIndustries] })}
                      className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50"
                      data-testid="config-industries-all"
                    >Select all</button>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 max-h-64 overflow-y-auto border rounded-md p-3 bg-slate-50">
                  {availableIndustries.map((ind) => {
                    const on = (config.allowed_industries || []).includes(ind);
                    return (
                      <label
                        key={ind}
                        className={`flex items-center gap-2 text-xs cursor-pointer px-2 py-1 rounded transition ${on ? "bg-cyan-100 text-cyan-800 font-semibold" : "hover:bg-white text-slate-600"}`}
                        data-testid={`config-industry-${ind.replace(/\s+/g, "-").toLowerCase()}`}
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={(e) => {
                            const next = new Set(config.allowed_industries || []);
                            if (e.target.checked) next.add(ind); else next.delete(ind);
                            setConfig({ ...config, allowed_industries: Array.from(next) });
                          }}
                          className="w-3 h-3"
                        />
                        <span className="truncate">{ind}</span>
                      </label>
                    );
                  })}
                  {availableIndustries.length === 0 && (
                    <div className="col-span-full text-xs text-slate-400 italic text-center py-4">
                      No industries seeded yet. The trending-topics scheduler populates this weekly.
                    </div>
                  )}
                </div>
              </div>

              <Button onClick={saveConfig} data-testid="config-save">Save configuration</Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── Preview / Plagiarism modal ── */}
      {preview && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {preview.loading ? (
              <div className="p-16 text-center text-slate-500 text-sm">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading…
              </div>
            ) : (
              <PreviewBody draft={preview} onClose={() => setPreview(null)} onCheck={() => { checkOriginality(preview.id); setTimeout(() => openPreview(preview.id), 8000); }} onApprove={async (dest) => { await approve(preview.id, dest); setPreview(null); }} onReject={async () => { await reject(preview.id); setPreview(null); }} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Full-fidelity preview of a draft with two tabs:
 *   • Document — full body content rendered as a clean readable article
 *   • Landing page — form + about + learn bullets (what visitors see)
 *  Plus a Plagiarism panel showing every matched phrase + source URL. */
function PreviewBody({ draft, onClose, onCheck, onApprove, onReject }) {
  const [previewMode, setPreviewMode] = useState("document");   // "document" | "landing"
  const sm = draft.source_meta || {};
  const plag = sm.plagiarism;
  const score = plag && typeof plag.score === "number" ? plag.score : null;
  const scoreTone = score === null ? "text-slate-400" : score >= 90 ? "text-emerald-600" : score >= 70 ? "text-amber-600" : "text-red-600";
  const quality = sm.quality || null;
  const qScore = quality && typeof quality.final_score === "number" ? quality.final_score : null;
  const qTone = qScore === null ? "text-slate-400" : qScore >= 85 ? "text-emerald-600" : qScore >= 70 ? "text-amber-600" : "text-red-600";
  const bodyHtml = (draft.blocks || []).filter((b) => b.type === "html").map((b) => b.html || "").join("\n");
  const learnBullets = Array.isArray(draft.learn_bullets) ? draft.learn_bullets : [];
  const aboutSummary = draft.about_summary || draft.abstract || "";
  const catLabels = { whitepaper: "whitepaper", ebook: "eBook", "case-study": "case study", "buyers-guide": "buyer's guide", blog: "article" };
  const catLabel = catLabels[draft.category] || "document";

  // Preview URLs — drafts require the unguessable `preview_key` (their own
  // UUID) so the Astro pages can bypass the "published only" filter.
  const origin = typeof window !== "undefined" ? window.location.origin : API;
  const isDraft = draft.status !== "published";
  const previewSuffix = `?slug=${encodeURIComponent(draft.slug)}&lang=${encodeURIComponent(draft.language || "en")}${isDraft ? `&preview_key=${encodeURIComponent(draft.id)}` : ""}`;
  const landing_url  = `${origin}/sbt-site/resources/detail/${previewSuffix}`;
  const document_url = `${origin}/sbt-site/resources/document/${previewSuffix}`;
  const preview_url = previewMode === "document" ? document_url : landing_url;

  const copyToClipboard = async (text, label) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older browsers / non-secure contexts
        const ta = document.createElement("textarea");
        ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success(`${label} link copied — share it with your reviewer.`);
    } catch (e) {
      toast.error("Copy failed — please copy manually from the URL row.");
    }
  };

  return (
    <>
      <div className="sticky top-0 z-10 bg-white border-b p-5 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Preview draft · {draft.slug}</div>
          <h3 className="text-lg font-semibold text-slate-900 mt-1">{draft.title}</h3>
        </div>
        <div className="flex gap-2 flex-wrap">
          {qScore !== null && (
            <div className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1.5 rounded border ${qTone}`} title={`Analyst quality — ${quality?.attempts || 1} attempt(s)`} data-testid="preview-quality-score">
              <Sparkles className="w-4 h-4" /> Quality {qScore}/100
            </div>
          )}
          {score !== null ? (
            <div className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1.5 rounded border ${scoreTone}`}>
              {score >= 70 ? <ShieldCheck className="w-4 h-4" /> : <ShieldAlert className="w-4 h-4" />} Originality {score}/100
            </div>
          ) : (
            <Button variant="outline" size="sm" onClick={onCheck}>
              <ShieldCheck className="w-4 h-4 mr-1" /> Run originality check
            </Button>
          )}
          <Button
            variant="outline" size="sm"
            onClick={() => window.open(`${API}/api/sbt-site/landing-page.pdf?slug=${encodeURIComponent(draft.slug)}&lang=${encodeURIComponent(draft.language || 'en')}&preview_key=${encodeURIComponent(draft.id)}`, '_blank', 'noopener')}
            data-testid="draft-preview-pdf"
          >
            <FileText className="w-4 h-4 mr-1" /> Download PDF
          </Button>
          <Button variant="outline" size="sm" onClick={onReject}><Trash2 className="w-4 h-4 mr-1" /> Reject</Button>
          <Button size="sm" onClick={() => onApprove("resources")} data-testid="preview-approve-resources"><CheckCircle2 className="w-4 h-4 mr-1" /> Publish to Resources</Button>
          <Button size="sm" variant="secondary" onClick={() => onApprove("market_plus")} data-testid="preview-approve-marketplus"><CheckCircle2 className="w-4 h-4 mr-1" /> Publish to Market Plus</Button>
          <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
        </div>
      </div>

      {/* Preview mode tabs — Document (content review) vs Landing page (visitor view) */}
      <div className="border-b bg-slate-50 px-6 py-3 flex items-center gap-3 flex-wrap" data-testid="preview-mode-tabs">
        <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500 mr-2">Preview mode:</div>
        <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden bg-white">
          <button
            type="button"
            className={`px-4 py-1.5 text-xs font-semibold transition ${previewMode === "document" ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}
            onClick={() => setPreviewMode("document")}
            data-testid="preview-mode-document"
          >
            <FileText className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5" /> Document
          </button>
          <button
            type="button"
            className={`px-4 py-1.5 text-xs font-semibold transition ${previewMode === "landing" ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}
            onClick={() => setPreviewMode("landing")}
            data-testid="preview-mode-landing"
          >
            <Eye className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5" /> Landing page
          </button>
        </div>
        <div className="text-[11px] text-slate-500 ml-2">
          {previewMode === "document"
            ? "Reviewing the actual whitepaper/eBook/guide content"
            : "Reviewing the visitor-facing lead-capture landing page"}
        </div>
        <div className="flex-1"></div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => copyToClipboard(preview_url, previewMode === "document" ? "Document" : "Landing page")}
          data-testid={`preview-copy-${previewMode}`}
          title="Copy shareable link — no expiry. Anyone with the URL can view."
        >
          <Copy className="w-3.5 h-3.5 mr-1" /> Copy link
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => window.open(preview_url, "_blank", "noopener")}
          data-testid={`preview-open-${previewMode}`}
        >
          <ExternalLink className="w-3.5 h-3.5 mr-1" /> Open in new tab
        </Button>
      </div>

      <div className="p-6 space-y-6">
        {/* Illustrative banner if applicable */}
        {sm.illustrative && (
          <div className="bg-amber-50 border-l-4 border-amber-500 p-3 rounded text-sm text-amber-900">
            <strong>⚠ Illustrative case study</strong> — this scenario is a template. It is not an SBT Research client claim until confirmed with {sm.company || "the client"}.
          </div>
        )}

        {previewMode === "document" ? (
          /* ── DOCUMENT PREVIEW ─────────────────────────────────
             Clean, article-style rendering of the whitepaper /
             eBook / guide body — no form, no signup. Mirrors what
             the /sbt-site/resources/document/ page shows. */
          <div className="bg-white rounded-lg border border-slate-200 p-8 md:p-10 shadow-sm" data-testid="preview-document-view">
            <div className="text-[10px] uppercase font-bold tracking-wider text-cyan-700 border border-cyan-200 bg-cyan-50 rounded-full inline-block px-3 py-1 mb-4">
              {catLabel.toUpperCase()}
            </div>
            <h1 className="font-serif text-3xl md:text-4xl font-extrabold text-slate-900 leading-tight mb-3" style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
              {draft.title}
            </h1>
            {aboutSummary && (
              <p className="text-slate-600 text-base leading-relaxed mb-6 max-w-[62ch]">
                {aboutSummary.split(/(?<=[.!?])\s+/)[0]}
              </p>
            )}
            {learnBullets.length > 0 && (
              <div className="bg-sky-50 border border-sky-200 rounded-lg p-5 mb-6">
                <div className="font-bold text-slate-900 mb-2">In this {catLabel}, you&apos;ll learn:</div>
                <ul className="space-y-1.5 text-slate-700 text-sm">
                  {learnBullets.map((b, i) => (
                    <li key={i} className="flex gap-2"><span className="text-sky-700 font-bold">→</span><span>{b}</span></li>
                  ))}
                </ul>
              </div>
            )}
            {bodyHtml ? (
              <div className="prose prose-sm md:prose-base max-w-none prose-headings:font-sans prose-headings:text-slate-900 prose-h3:mt-8 prose-h3:pt-4 prose-h3:border-t prose-h3:border-slate-200 prose-h3:first:mt-0 prose-h3:first:pt-0 prose-h3:first:border-t-0 prose-p:text-slate-700 prose-p:leading-relaxed prose-strong:text-slate-900 prose-table:text-sm prose-th:bg-slate-100 prose-td:border prose-td:border-slate-200 prose-th:border prose-th:border-slate-200"
                dangerouslySetInnerHTML={{ __html: bodyHtml }}
              />
            ) : (
              <p className="text-slate-400 italic">No body content yet.</p>
            )}
          </div>
        ) : (
          /* ── LANDING PAGE PREVIEW ──────────────────────────────
             Compact rendering that mirrors the /resources/detail/
             layout (title + about + learn bullets + form CTA). */
          <div className="bg-white rounded-lg border border-slate-200 p-8" data-testid="preview-landing-view">
            <div className="grid md:grid-cols-3 gap-8">
              <div className="md:col-span-2 space-y-5">
                <div className="text-[10px] uppercase font-bold tracking-wider text-cyan-700">
                  {catLabel.toUpperCase()}
                </div>
                <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 leading-tight" style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                  {draft.title}
                </h1>
                <div>
                  <div className="text-lg font-bold text-slate-900 mb-2">About the {catLabel}:</div>
                  <p className="text-slate-700 text-sm leading-relaxed">
                    {aboutSummary.length > 250 ? aboutSummary.slice(0, 250).replace(/\s+\S*$/, "") + "…" : aboutSummary}
                  </p>
                </div>
                {learnBullets.length > 0 && (
                  <div>
                    <div className="text-lg font-bold text-slate-900 mb-2">In this {catLabel}, you&apos;ll learn:</div>
                    <ul className="space-y-1 text-sm text-slate-700">
                      {learnBullets.map((b, i) => (
                        <li key={i} className="flex gap-2"><span className="text-cyan-700">—</span><span>{b}</span></li>
                      ))}
                    </ul>
                  </div>
                )}
                {(draft.highlights || []).length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Highlights</div>
                    <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700">
                      {draft.highlights.slice(0, 8).map((h, i) => <li key={i}>{h}</li>)}
                    </ul>
                  </div>
                )}
              </div>
              <aside className="md:col-span-1">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-5 sticky top-4">
                  <div className="text-xs font-bold text-slate-800 mb-3">Get the {catLabel}</div>
                  <div className="space-y-2 text-xs text-slate-500">
                    <div className="bg-white border border-slate-200 rounded px-2 py-1.5">Business Email *</div>
                    <div className="bg-white border border-slate-200 rounded px-2 py-1.5">First Name *</div>
                    <div className="bg-white border border-slate-200 rounded px-2 py-1.5">Last Name *</div>
                    <div className="bg-white border border-slate-200 rounded px-2 py-1.5">Country *</div>
                    <div className="bg-white border border-slate-200 rounded px-2 py-1.5">Company Name *</div>
                    <div className="bg-white border border-slate-200 rounded px-2 py-1.5">Job Title *</div>
                  </div>
                  <div className="mt-3 bg-cyan-600 text-white text-center text-sm font-bold py-2 rounded">
                    {draft.cta_label || "Download Now"}
                  </div>
                </div>
              </aside>
            </div>
          </div>
        )}

        {/* Meta strip (compact) */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs pt-2 border-t">
          <div><div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Category</div><div className="mt-1 font-semibold text-slate-800">{draft.category}</div></div>
          {sm.industry && <div><div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Industry</div><div className="mt-1 font-semibold text-slate-800">{sm.industry}</div></div>}
          {sm.topic && <div className="col-span-2"><div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Topic</div><div className="mt-1 font-semibold text-slate-800">{sm.topic}</div></div>}
          {sm.company && <div><div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Company</div><div className="mt-1 font-semibold text-slate-800">{sm.company}</div></div>}
          <div><div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Generated</div><div className="mt-1 text-slate-800">{sm.generated_at ? new Date(sm.generated_at).toLocaleString() : ""}</div></div>
        </div>

        {/* Plagiarism panel */}
        {plag && (
          <div className="border-t pt-5">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" /> Originality report
            </div>
            <div className="text-sm text-slate-700 mb-3">
              Score: <span className={`font-bold ${scoreTone}`}>{plag.score}/100</span> · Phrases checked: {plag.phrases_checked} · Duplicated: {plag.phrases_duplicated}
              {plag.score !== null && plag.score < 60 && (
                <div className="mt-2 text-xs text-red-600 font-semibold">⚠ Publishing is blocked at &lt;60. Rewrite duplicated phrases or force-publish from the card.</div>
              )}
            </div>
            {(plag.matches || []).length > 0 ? (
              <div className="space-y-3">
                {plag.matches.map((m, i) => (
                  <div key={i} className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="text-xs italic text-slate-700 mb-2">&ldquo;{m.phrase}&rdquo;</div>
                    <div className="text-[11px] uppercase tracking-wider text-red-600 font-semibold mb-1">Matched on:</div>
                    <ul className="space-y-1 text-xs">
                      {(m.hits || []).map((h, j) => (
                        <li key={j}>
                          <a href={h.url} target="_blank" rel="noopener noreferrer" className="text-cyan-700 hover:underline inline-flex items-center gap-1">
                            <ExternalLink className="w-3 h-3" /> {h.title || h.url}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                ✓ No exact-match duplicates found on Google for any of the {plag.phrases_checked} sampled phrases.
              </div>
            )}
          </div>
        )}

        <div className="text-[11px] text-slate-500 pt-3 border-t space-y-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Shareable preview links (no expiry, no login required)</div>
          <div className="flex items-center gap-2 flex-wrap" data-testid="shareable-document-link">
            <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded bg-slate-900 text-white flex-shrink-0">
              <FileText className="w-3 h-3" /> Document
            </span>
            <a href={document_url} target="_blank" rel="noopener noreferrer" className="text-cyan-700 hover:underline truncate flex-1 min-w-0">{document_url}</a>
            <Button variant="ghost" size="sm" onClick={() => copyToClipboard(document_url, "Document")} data-testid="copy-doc-link" className="h-7 px-2 text-xs flex-shrink-0">
              <Copy className="w-3 h-3 mr-1" /> Copy
            </Button>
          </div>
          <div className="flex items-center gap-2 flex-wrap" data-testid="shareable-landing-link">
            <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded bg-cyan-600 text-white flex-shrink-0">
              <Eye className="w-3 h-3" /> Landing page
            </span>
            <a href={landing_url} target="_blank" rel="noopener noreferrer" className="text-cyan-700 hover:underline truncate flex-1 min-w-0">{landing_url}</a>
            <Button variant="ghost" size="sm" onClick={() => copyToClipboard(landing_url, "Landing page")} data-testid="copy-landing-link" className="h-7 px-2 text-xs flex-shrink-0">
              <Copy className="w-3 h-3 mr-1" /> Copy
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

/** Newsletter-in-a-Box — mines pain points from a target company (SEC +
 *  SerpAPI + web scrape), drafts a personalised newsletter, publishes to
 *  Resources, pings IndexNow + Google Indexing API. Total ~60-90s. */
function NewsletterPanel({ hdr, onGenerated }) {
  const [companies, setCompanies] = useState("");
  const [persona, setPersona] = useState("");
  const [industry, setIndustry] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    const list = companies.split(",").map((s) => s.trim()).filter(Boolean);
    if (!list.length && !persona.trim()) {
      toast.error("Provide at least one target company or a persona");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const r = await fetch(`${API}/api/admin/sbt/newsletter/generate`, {
        method: "POST", headers: hdr(),
        body: JSON.stringify({ companies: list, persona, industry, notes }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "failed");
      setResult(j);
      toast.success(`Newsletter drafted in ${j.duration_seconds?.toFixed(1)}s — see Draft Inbox`);
      onGenerated?.();
    } catch (e) { toast.error("Generation failed: " + e.message); }
    finally { setBusy(false); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <FileText className="w-4 h-4 text-rose-500" /> Newsletter in a Box
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-sm text-slate-600">
          Mines strategic priorities + business challenges from SEC 10-K filings + Google search + company websites, synthesises the buyer persona, and drafts a targeted newsletter — then publishes under Resources and pings IndexNow (Bing indexes within ~5-15 min) + Google Indexing API (1-4 hour Google visibility).
        </div>
        <div>
          <Label className="text-xs">Target company / companies <span className="text-slate-400">(comma-sep, up to 3 — leave blank to use persona-only)</span></Label>
          <Input value={companies} onChange={(e) => setCompanies(e.target.value)} placeholder="Salesforce, Adobe, HubSpot" data-testid="nl-companies" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Persona <span className="text-slate-400">(optional if company set)</span></Label>
            <Input value={persona} onChange={(e) => setPersona(e.target.value)} placeholder="VP Demand Generation" data-testid="nl-persona" />
          </div>
          <div>
            <Label className="text-xs">Industry</Label>
            <Input value={industry} onChange={(e) => setIndustry(e.target.value)} placeholder="MarTech / FinTech / …" data-testid="nl-industry" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Context notes <span className="text-slate-400">(optional — angle, campaign, tone)</span></Label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Focus on install-base displacement; friendly tone" data-testid="nl-notes" />
        </div>
        <Button onClick={submit} disabled={busy} data-testid="nl-generate">
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
          {busy ? "Mining + drafting (~60s)…" : "Generate newsletter"}
        </Button>

        {result && (
          <div className="border-t pt-4 space-y-2 text-xs">
            <div><strong>Subject:</strong> {result.subject_line}</div>
            <div><strong>Persona:</strong> {result.persona?.persona || "—"}</div>
            <div><strong>Pain points mined:</strong>
              <ul className="list-disc pl-5 mt-1">
                {(result.persona?.top_3_pain_points || []).map((p, i) => <li key={i}>{p}</li>)}
              </ul>
            </div>
            <div>
              <strong>Duration:</strong> {result.duration_seconds?.toFixed(1)}s ·
              <strong className="ml-2">IndexNow:</strong> HTTP {result.indexnow?.status} ·
              <strong className="ml-2">Google Indexing:</strong> {result.google_indexing?.skipped ? "skipped (add creds)" : `HTTP ${result.google_indexing?.status}`}
            </div>
            <div><strong>Public URL (after approval):</strong> <a className="text-cyan-700 underline" href={result.url} target="_blank" rel="noopener noreferrer">{result.url}</a></div>
            <div className="text-slate-500 italic">Draft is now in the Draft Inbox with an originality score. Review + approve to publish.</div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
